import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { ReferralTarget, ReferralTargetPractitioner } from '@xmedical/core';

interface SearchResultItem {
  practice: ReferralTarget;
  practitioner: ReferralTargetPractitioner | null;
  displayName: string;
  subText: string;
}

const REFERRAL_MODE_STANDARD = 1;

@Component({
  selector: 'xm-practice-target-selector',
  imports: [
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
  ],
  templateUrl: './practice-target-selector.component.html',
  styleUrl: './practice-target-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PracticeTargetSelectorComponent {
  // Inputs
  targets = input<ReferralTarget[]>([]);
  selectedPractice = input<ReferralTarget | null>(null);
  selectedPractitioner = input<ReferralTargetPractitioner | null>(null);
  title = input('Select Recipient');
  required = input(false);

  // Outputs
  selectedPracticeChange = output<ReferralTarget | null>();
  selectedPractitionerChange = output<ReferralTargetPractitioner | null>();

  // Internal state
  searchQuery = signal('');
  showResults = signal(false);

  private allResults = computed<SearchResultItem[]>(() => {
    const results: SearchResultItem[] = [];
    for (const practice of this.targets()) {
      if (practice.referralMode === REFERRAL_MODE_STANDARD) {
        for (const practitioner of practice.practitioners) {
          results.push({
            practice,
            practitioner,
            displayName: practitioner.name,
            subText: practice.practiceName,
          });
        }
      } else {
        results.push({
          practice,
          practitioner: null,
          displayName: practice.practiceName,
          subText: 'Facility',
        });
      }
    }
    return results;
  });

  filteredResults = computed<SearchResultItem[]>(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (query.length < 2) return [];
    return this.allResults().filter(item =>
      item.displayName.toLowerCase().includes(query) ||
      item.subText.toLowerCase().includes(query)
    );
  });

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery.set(value);
    this.showResults.set(value.length >= 2);
  }

  clearSearch(): void {
    this.searchQuery.set('');
    this.showResults.set(false);
  }

  selectItem(item: SearchResultItem): void {
    this.selectedPracticeChange.emit(item.practice);
    this.selectedPractitionerChange.emit(item.practitioner);
    this.clearSearch();
  }

  clearSelection(): void {
    this.selectedPracticeChange.emit(null);
    this.selectedPractitionerChange.emit(null);
  }

  get hasSelection(): boolean {
    return this.selectedPractice() !== null;
  }
}
